import clsx from "clsx";
import { startCase } from "lodash";
import { FC, MouseEventHandler, PropsWithChildren } from "react";
import { useRecoilValue } from "recoil";

import { SVG_MUI } from "@ryde/assets";
import { useCrudEditAdminContext, useCrudEditContext } from "@ryde/redux";
import {
  IExtendableDto,
  isInnerItemEditingAtom,
  useDrawers,
  useIsMobile,
} from "@ryde/shared";

import { HeaderRightButtonsStyled } from "../header";
import {
  LayoutRightHeaderStartButtonsStyled,
  LayoutRightHeaderStyled,
  LayoutRightHeaderTitleStyled,
} from "./layout.right.styled";
import { ButtonHeader, LayoutRightHistoryButton } from "./partials";

interface ILayoutRightHeaderProps extends PropsWithChildren {
  headerTitle?: string;
  subHeaderTitle?: string;
  closeClick?: MouseEventHandler<Element>;
  headerRightContent?: React.ReactNode;
}

export const LayoutRightHeader: FC<ILayoutRightHeaderProps> = ({
  headerTitle,
  subHeaderTitle,
  closeClick,
  headerRightContent,
}) => {
  const { isMobile } = useIsMobile();
  useDrawers();

  // Try to get context from regular CRUD first
  const regularContext = useCrudEditContext<IExtendableDto>();

  // Try to get context from admin CRUD
  const adminContext = useCrudEditAdminContext<IExtendableDto>();

  // Use whichever context is available (check admin first since admin forms are more specific)
  const isAdminContext =
    adminContext.entity && adminContext.entity !== "unknown";
  const isRegularContext =
    regularContext.entity && regularContext.entity !== "unknown";

  let data;
  let entity;

  if (isAdminContext) {
    data = adminContext.data;
    entity = adminContext.entity;
  } else if (isRegularContext) {
    data = regularContext.data;
    entity = regularContext.entity;
  } else {
    data = adminContext.data;
    entity = adminContext.entity;
  }

  const dto = data?.dto;

  const isInnerItemEditing = useRecoilValue(isInnerItemEditingAtom);

  return (
    <LayoutRightHeaderStyled
      className={clsx(isInnerItemEditing && "inner-item-editing")}
    >
      <LayoutRightHeaderStartButtonsStyled>
        {closeClick && !isMobile && (
          <ButtonHeader onClick={closeClick} aria-label="Close">
            <SVG_MUI.XMarkSVG aria-hidden={true} data-e2e="close-button" />
          </ButtonHeader>
        )}
      </LayoutRightHeaderStartButtonsStyled>
      <LayoutRightHeaderTitleStyled>
        {startCase(headerTitle)}
        {subHeaderTitle && <span className="sub_header">{subHeaderTitle}</span>}
      </LayoutRightHeaderTitleStyled>
      <HeaderRightButtonsStyled>
        <LayoutRightHistoryButton entity={entity} dtoId={dto?.id} />
        {headerRightContent}
        {closeClick && isMobile && (
          <ButtonHeader onClick={closeClick}>
            <SVG_MUI.XMarkSVG data-e2e="close-button" />
          </ButtonHeader>
        )}
      </HeaderRightButtonsStyled>
    </LayoutRightHeaderStyled>
  );
};
